<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <loading-spinner v-if="isLoading"></loading-spinner>
        <v-card v-else class="rounded-lg">
          <card-header
            :title="$t('title.journal_edit')"
            icon="menu"
            color="primary"
            variant="gradient"
          ></card-header>

          <v-form ref="form" v-model="isValid">
            <v-card-text>
              <v-row>
                <v-col class="d-flex justify-center align-center">
                  <!-- 숨겨진 파일 입력 -->
                  <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    @change="handleFileChange"
                    style="display: none"
                  />

                  <!-- 클릭 가능한 카드 영역 -->
                  <v-card
                    width="200"
                    height="250"
                    class="image-upload-card d-flex justify-center align-center"
                    @click="triggerFileInput"
                  >
                    <template v-if="journalImage && imagePreview">
                      <v-img
                        :src="imagePreview"
                        max-width="200"
                        max-height="250"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                            ></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </template>
                    <template v-else-if="imageBlob">
                      <v-img
                        :src="imageBlob"
                        max-width="200"
                        max-height="250"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                            ></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </template>
                    <template v-else>
                      <div class="text-center">
                        <v-icon size="64" color="grey lighten-1"
                          >mdi-image-plus</v-icon
                        >
                        <div class="mt-2 grey--text">
                          {{ $t("txt.click_to_upload") }}
                        </div>
                      </div>
                    </template>
                  </v-card>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    :value="journal.journal_type?.name"
                    :label="$t('label.journal_category')"
                    prepend-icon="book"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.title"
                    :label="$t('label.journal_title')"
                    prepend-icon="title"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.title_eng"
                    :label="$t('label.journal_title_eng')"
                    prepend-icon="title"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row v-show="isJournalType">
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.issn"
                    :label="$t('label.journal_issn')"
                    prepend-icon="notes"
                    :rules="isJournalType ? [rules.required] : []"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.eissn"
                    :label="$t('label.journal_eissn')"
                    prepend-icon="notes"
                    :rules="isJournalType ? [rules.required] : []"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <default-date-picker
                    v-model="journal.submission_start_date"
                    :label="$t('label.journal_submission_start_date')"
                    :rules="[rules.required]"
                  ></default-date-picker>
                </v-col>

                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <default-date-picker
                    v-model="journal.submission_end_date"
                    :label="$t('label.journal_submission_end_date')"
                    :rules="[rules.required]"
                  ></default-date-picker>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="researchEthicsStatement"
                    :label="$t('label.research_ethics_statement')"
                    prepend-icon="notes"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="copyrightTransferAgreement"
                    :label="$t('label.copyright_transfer_agreement')"
                    prepend-icon="notes"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="plagiarismCheckAgreement"
                    :label="$t('label.plagiarism_check_agreement')"
                    prepend-icon="notes"
                    readonly
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  xs="12"
                  sm="6"
                  md="6"
                  lg="6"
                  xl="6"
                  class="mt-4"
                >
                  <default-date-picker
                    v-model="journal.publish_date"
                    :label="$t('label.journal_publish_date')"
                  ></default-date-picker>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.year"
                    :label="$t('label.journal_year')"
                    type="number"
                    prepend-icon="notes"
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.volume"
                    :label="$t('label.journal_volume')"
                    type="number"
                    prepend-icon="notes"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="6" xl="6">
                  <v-text-field
                    v-model="journal.issue"
                    :label="$t('label.journal_issue')"
                    type="number"
                    prepend-icon="notes"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  xs="12"
                  sm="6"
                  md="6"
                  lg="6"
                  xl="6"
                  v-if="isJournalType"
                >
                  <v-text-field
                    v-model="journal.doi_suffix"
                    :label="$t('label.journal_doi_suffix')"
                    prepend-icon="notes"
                    :rules="isJournalType ? [rules.required] : []"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="journal.description"
                    :label="$t('label.journal_description')"
                    outlined
                    dense
                    :rules="[rules.required]"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="d-flex align-center justify-space-between">
              <custom-button
                @click="toJournalManagement"
                :label="$t('button.cancel')"
                color="black"
                size="lg"
                gradient
                rounded
              ></custom-button>
              <custom-button
                @click="updateJournal"
                :disabled="!isValid"
                :label="$t('button.save')"
                color="primary"
                size="lg"
                gradient
                rounded
              ></custom-button>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import CardHeader from "@/components/cards/CardHeader.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";
import DefaultDatePicker from "@/components/common/Pickers/DefaultDatePicker.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";
import { journalApi, fileApi } from "@/api/api";
import { confirmClosePopup, confirmSavePopup } from "@/fields/journal/popups";

export default {
  components: {
    CardHeader,
    CustomButton,
    DefaultDatePicker,
    LoadingSpinner,
  },
  props: {
    idx: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isValid: false,
      journal: {
        title: "",
        title_eng: "",
        journal_type_idx: "",
        image_path: null,
        description: "",
        issn: null,
        eissn: null,
        year: null,
        volume: null,
        issue: null,
        research_ethics_statement: null,
        copyright_transfer_agreement: null,
        plagiarism_check_agreement: null,
        submission_start_date: "",
        submission_end_date: "",
        publish_date: "",
        doi_suffix: null,
      },
      originalImagePath: null,
      imageBlob: null,
      imagePreview: null,
      journalImage: null,
      journalTypes: [],
      rules: {
        required: (value) => !!value || this.$t("valid.required_field"),
      },
      isLoading: false,
    };
  },
  watch: {
    "journal.image_path": {
      immediate: true,
      handler(newPath) {
        if (newPath && newPath !== "" && !this.journalImage) {
          this.originalImagePath = newPath;
          this.loadImage();
        }
      },
    },
    "journal.publish_date"(newVal) {
      if (typeof newVal === "string") {
        const date = new Date(newVal);
        if (!isNaN(date)) {
          this.journal.year = date.getFullYear();
        }
      }
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async loadImage() {
      if (!this.journal.image_path || this.journal.image_path === "") {
        return;
      }

      try {
        const response = await fileApi.fileDownload({
          file_name: this.journal.image_path,
        });
        if (this.imageBlob) {
          URL.revokeObjectURL(this.imageBlob);
        }
        this.imageBlob = URL.createObjectURL(response.data);
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_fetch_image"),
          style: "danger",
        });
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0];

      if (file) {
        this.journalImage = file;
        if (this.imageBlob) {
          URL.revokeObjectURL(this.imageBlob);
          this.imageBlob = null;
        }
        this.previewImage(file);
      }
    },
    previewImage(file) {
      if (!file) {
        this.imagePreview = null;
        return;
      }
      this.imagePreview = URL.createObjectURL(file);
    },

    async updateJournal() {
      const isConfirm = await confirmSavePopup();

      if (isConfirm) {
        const isValid = await this.$refs.form.validate();

        if (isValid) {
          this.isLoading = true;
          try {
            if (this.journalImage) {
              await this.uploadFile();

              if (this.originalImagePath) {
                await fileApi.deleteFile({
                  file_name: this.originalImagePath,
                });
              }
            }

            const body = {
              idx: this.journal.idx,
              title: this.journal.title,
              title_eng: this.journal.title_eng,
              image_path: this.journal.image_path,
              description: this.journal.description,
              issn: this.journal.issn,
              eissn: this.journal.eissn,
              year: this.journal.year,
              volume: this.journal.volume,
              issue: this.journal.issue,
              submission_start_date: this.journal.submission_start_date,
              submission_end_date: this.journal.submission_end_date,
              publish_date: this.journal.publish_date,
              doi_suffix: this.journal.doi_suffix,
            };
            const response = await journalApi.updateJournal(body);
            if (response.status === 200) {
              this.notifyVue({
                message: this.$t("alert.success_update_journal"),
                style: "success",
              });
              this.$router.push({ name: "journals-management" });
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            this.notifyVue({
              message: this.$t("alert.failed_request"),
              style: "danger",
            });
          } finally {
            this.isLoading = false;
          }
        }
      }
    },
    async toJournalManagement() {
      const isConfirm = await confirmClosePopup();

      if (isConfirm) {
        this.$router.push({ name: "journals-management" });
      }
    },
    async uploadFile() {
      try {
        const formData = new FormData();
        formData.append("file", this.journalImage);
        formData.append("category", "journal");

        const response = await fileApi.uploadFile(formData);
        if (response.status === 200) {
          this.journal.image_path = response.data.path;
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
        throw error;
      }
    },
    async fetchJournal() {
      try {
        this.isLoading = true;
        const params = {
          idx: this.idx,
        };
        const response = await journalApi.showJournal(params);
        if (response.status === 200) {
          this.journal = response.data.response;
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
        this.$router.push({ name: "journals-management" });
      } finally {
        this.isLoading = false;
      }
    },

    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
    } = {}) {
      this.$notify({
        timeout: 8000,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  computed: {
    researchEthicsStatement() {
      return (
        this.journal.consent_documents?.find(
          (doc) => doc.document_type_code === "research_ethics_statement"
        )?.title || ""
      );
    },
    copyrightTransferAgreement() {
      return (
        this.journal.consent_documents?.find(
          (doc) => doc.document_type_code === "copyright_transfer_agreement"
        )?.title || ""
      );
    },
    plagiarismCheckAgreement() {
      return (
        this.journal.consent_documents?.find(
          (doc) => doc.document_type_code === "plagiarism_check_agreement"
        )?.title || ""
      );
    },
    isJournalType() {
      return this.journal.journal_type?.code === "jnl";
    },
  },
  mounted() {
    this.fetchJournal();
  },
  beforeDestroy() {
    if (this.imageBlob) {
      URL.revokeObjectURL(this.imageBlob);
    }
    if (this.imagePreview) {
      URL.revokeObjectURL(this.imagePreview);
    }
  },
};
</script>

<style scoped>
.image-upload-card {
  border: 2px dashed #e0e0e0;
  transition: all 0.3s;
  overflow: hidden;
  cursor: pointer;
}

.image-upload-card:hover {
  border-color: #1976d2;
  background-color: #f5f5f5;
}

.image-upload-card .v-icon {
  transition: all 0.3s;
}

.image-upload-card:hover .v-icon {
  color: #1976d2 !important;
  transform: scale(1.1);
}
</style>
